import { getMeta } from '../db/local.js';
import { normalizeComparableUom } from '@trackeat/shared';
import {
  enqueueAtomicRpcV1,
  type AtomicRpcHandle,
} from '../sync/atomicRpcOutbox.js';

/**
 * A deliberately narrow recovery request.  The server derives the food,
 * supplier, document, lot, expiry and carried cost from `source_event_id`;
 * the client only attests the physical count it has observed.
 */
export const HISTORICAL_LOT_RECOUNT_SCHEMA =
  'trackeat.consultant-historical-lot-recount' as const;
export const HISTORICAL_LOT_RECOUNT_RESULT_SCHEMA =
  'trackeat.consultant-historical-lot-recount-result' as const;

export type HistoricalLotRecountConversionSource =
  | 'identity'
  | 'metric'
  | 'document_pack'
  | 'document_weight'
  | 'supplier_alias'
  | 'user_confirmed'
  | 'carried';

export interface HistoricalLotBalanceExpectationV1 {
  /** The historical recovery is valid only for a zero balance. */
  qty: number;
  event_count: number;
  /** Local events are keyed by the immutable client UUID. */
  last_event_client_uuid: string;
  closed: boolean;
}

export interface ConsultantHistoricalLotRecountRequestV1
  extends Record<string, unknown> {
  schema: typeof HISTORICAL_LOT_RECOUNT_SCHEMA;
  version: 1;
  request_id: string;
  tenant_id: string;
  business_id: string;
  source_event_id: string;
  expected_balance: HistoricalLotBalanceExpectationV1;
  counted_stock_qty: number;
  stock_uom: string;
  counted_source_qty: number;
  counted_source_uom: string;
  conversion_factor: number;
  conversion_source: HistoricalLotRecountConversionSource;
  counted_at: string;
  operator_name: string;
  note: string;
  device_id: string;
}

export interface BuildConsultantHistoricalLotRecountInputV1 {
  requestId: string;
  tenantId: string;
  businessId: string;
  sourceEventId: string;
  expectedBalance: HistoricalLotBalanceExpectationV1;
  countedStockQty: number;
  stockUom: string;
  countedSourceQty: number;
  countedSourceUom: string;
  conversionFactor: number;
  conversionSource: HistoricalLotRecountConversionSource;
  countedAt: string;
  operatorName: string;
  note: string;
  deviceId: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EPSILON = 1e-9;

function cleanText(value: string, field: string, max: number): string {
  const clean = value.trim();
  if (!clean || clean.length > max) {
    throw new Error(`historical_lot_recount_invalid_${field}`);
  }
  return clean;
}

function requireUuid(value: string, field: string): string {
  const clean = cleanText(value, field, 36);
  if (!UUID_RE.test(clean)) {
    throw new Error(`historical_lot_recount_invalid_${field}`);
  }
  return clean;
}

function requirePositive(value: number, field: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`historical_lot_recount_invalid_${field}`);
  }
  return value;
}

function nearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) <= EPSILON * Math.max(1, Math.abs(left), Math.abs(right));
}

function metricFactor(from: string, to: string): number | null {
  const normalizedFrom = normalizeComparableUom(from);
  const normalizedTo = normalizeComparableUom(to);
  const mass: Record<string, number> = { kg: 1, g: 0.001 };
  const volume: Record<string, number> = { l: 1, ml: 0.001 };
  if (mass[normalizedFrom] != null && mass[normalizedTo] != null) {
    return mass[normalizedFrom] / mass[normalizedTo];
  }
  if (volume[normalizedFrom] != null && volume[normalizedTo] != null) {
    return volume[normalizedFrom] / volume[normalizedTo];
  }
  return null;
}

/**
 * One physical locator can retain more than one historical document source.
 * The optimistic snapshot is the shared invariant, so it deliberately owns
 * the durable outbox scope rather than one particular source event.
 */
export function consultantHistoricalLotRecountScopeKey(input: {
  businessId: string;
  lastEventClientUuid: string;
}): string {
  return `historical-lot:${input.businessId}:${input.lastEventClientUuid}`;
}

/**
 * Validates the physical units before writing the durable atomic request.
 * A carton is intentionally represented as source coordinates, not hidden in
 * a note: `12 collo × 5.3 kg = 63.6 kg` remains machine-readable audit data.
 */
export function buildConsultantHistoricalLotRecountRequestV1(
  input: BuildConsultantHistoricalLotRecountInputV1,
): ConsultantHistoricalLotRecountRequestV1 {
  const requestId = requireUuid(input.requestId, 'request_id');
  const tenantId = requireUuid(input.tenantId, 'tenant_id');
  const businessId = requireUuid(input.businessId, 'business_id');
  const sourceEventId = requireUuid(input.sourceEventId, 'source_event_id');
  const stockUom = cleanText(input.stockUom, 'stock_uom', 10).toLowerCase();
  const sourceUom = cleanText(input.countedSourceUom, 'counted_source_uom', 10)
    .toLowerCase();
  const stockQty = requirePositive(input.countedStockQty, 'counted_stock_qty');
  const sourceQty = requirePositive(input.countedSourceQty, 'counted_source_qty');
  const factor = requirePositive(input.conversionFactor, 'conversion_factor');
  const operatorName = cleanText(input.operatorName, 'operator_name', 200);
  const note = cleanText(input.note, 'note', 500);
  const deviceId = cleanText(input.deviceId, 'device_id', 200);
  const expected = input.expectedBalance;

  if (
    !Number.isFinite(expected.qty)
    || Math.abs(expected.qty) > EPSILON
    || !Number.isSafeInteger(expected.event_count)
    || expected.event_count <= 0
    || !UUID_RE.test(expected.last_event_client_uuid)
    || typeof expected.closed !== 'boolean'
  ) {
    throw new Error('historical_lot_recount_invalid_expected_balance');
  }
  if (!nearlyEqual(sourceQty * factor, stockQty)) {
    throw new Error('historical_lot_recount_conversion_mismatch');
  }

  const comparableSourceUom = normalizeComparableUom(sourceUom);
  const comparableStockUom = normalizeComparableUom(stockUom);
  const expectedMetric = metricFactor(sourceUom, stockUom);
  if (comparableSourceUom === comparableStockUom) {
    if (input.conversionSource === 'metric') {
      throw new Error('historical_lot_recount_invalid_conversion_source');
    }
    if (input.conversionSource === 'identity' && !nearlyEqual(factor, 1)) {
      throw new Error('historical_lot_recount_invalid_conversion_factor');
    }
  } else if (expectedMetric != null) {
    if (
      input.conversionSource !== 'metric'
      || !nearlyEqual(factor, expectedMetric)
    ) {
      throw new Error('historical_lot_recount_invalid_metric_conversion');
    }
  } else if (
    input.conversionSource === 'identity'
    || input.conversionSource === 'metric'
  ) {
    throw new Error('historical_lot_recount_explicit_conversion_required');
  }

  const countedAt = new Date(input.countedAt);
  if (Number.isNaN(countedAt.getTime())) {
    throw new Error('historical_lot_recount_invalid_counted_at');
  }

  return {
    schema: HISTORICAL_LOT_RECOUNT_SCHEMA,
    version: 1,
    request_id: requestId,
    tenant_id: tenantId,
    business_id: businessId,
    source_event_id: sourceEventId,
    expected_balance: {
      qty: 0,
      event_count: expected.event_count,
      last_event_client_uuid: expected.last_event_client_uuid,
      closed: expected.closed,
    },
    counted_stock_qty: stockQty,
    stock_uom: stockUom,
    counted_source_qty: sourceQty,
    counted_source_uom: sourceUom,
    conversion_factor: factor,
    conversion_source: input.conversionSource,
    counted_at: countedAt.toISOString(),
    operator_name: operatorName,
    note,
    device_id: deviceId,
  };
}

export async function queueConsultantHistoricalLotRecountV1(
  input: Omit<BuildConsultantHistoricalLotRecountInputV1, 'deviceId'>,
): Promise<AtomicRpcHandle> {
  const deviceId = (await getMeta('device_id')) ?? 'unknown';
  const request = buildConsultantHistoricalLotRecountRequestV1({
    ...input,
    deviceId,
  });
  const queued = await enqueueAtomicRpcV1({
    kind: 'consultant_historical_lot_recount_v1',
    tenantId: request.tenant_id,
    businessId: request.business_id,
    // A stale server-side CAS rejection remains inspectable in the durable
    // queue, but it must not prevent a consultant from taking a fresh count
    // after pull. The observed last event makes that fresh snapshot a new,
    // still idempotent scope. It also blocks two distinct DDT sources from
    // reopening the same lot while offline.
    scopeKey: consultantHistoricalLotRecountScopeKey({
      businessId: request.business_id,
      lastEventClientUuid: request.expected_balance.last_event_client_uuid,
    }),
    request,
    optimisticMasters: [],
    now: request.counted_at,
  });
  return queued.handle;
}

export function parseConsultantHistoricalLotRecountResultV1(
  value: unknown,
  request: ConsultantHistoricalLotRecountRequestV1,
  requestDigest: string,
): void {
  if (!value || typeof value !== 'object') {
    throw new Error('historical_lot_recount_invalid_receipt');
  }
  const result = value as Record<string, unknown>;
  if (
    result.schema !== HISTORICAL_LOT_RECOUNT_RESULT_SCHEMA
    || result.version !== 1
    || result.request_id !== request.request_id
    || result.request_digest !== requestDigest
    || result.tenant_id !== request.tenant_id
    || result.business_id !== request.business_id
    || result.source_event_id !== request.source_event_id
    || typeof result.replayed !== 'boolean'
  ) {
    throw new Error('historical_lot_recount_receipt_mismatch');
  }
}
